import { ReactNode, useCallback, useMemo } from "react";

import useUIConfig from "hooks/useUIConfig";
import useAnalytics, { AnalyticsCommonProps } from "hooks/useAnalytics";
import { AnalyticsPageDashboard } from "hooks/useAnalytics/pages/dashboard";
import NotFoundPage from "components/error/NotFoundPage";
import useTypedFlags from "hooks/useTypedFlags";
import useTypedContext from "hooks/useTypedContext";
import { AccountContext } from "views/AccountWrapper";

import { DashboardColumns, DashboardConfig } from "./types";
import { DashboardContext } from "./context";
import { getInitialConfig } from "./helpers";
import { VIEWS_LIMIT, DASHBOARD_STORAGE_KEY } from "./constants";
import { validateConfig } from "./validateConfig";
import { showDashboardManageDrawer } from "./ManageDrawer";

type DashboardContextProviderProps = {
  children: ReactNode;
};

const DashboardContextProvider = ({ children }: DashboardContextProviderProps) => {
  const {
    numberOfDeploymentsWidgetFrontend,
    approvalPolicyEvaluationsWidgetFrontend,
    opentofuBackend,
  } = useTypedFlags();
  const { viewer } = useTypedContext(AccountContext);

  const flagsConfig = {
    numberOfDeploymentsWidgetFrontend,
    approvalPolicyEvaluationsWidgetFrontend,
    opentofuBackend,
  };

  const initialConfig = getInitialConfig(flagsConfig, viewer.admin);

  const { config, updateConfig, configLoading, noData } = useUIConfig<DashboardConfig>(
    DASHBOARD_STORAGE_KEY,
    initialConfig,
    validateConfig(flagsConfig, viewer.admin)
  );

  const trackSegmentAnalyticsEvent = useAnalytics({
    page: AnalyticsPageDashboard.Dashboard,
  });

  const tabs = useMemo(
    () =>
      Object.entries(config).map(([id, value]) => ({
        id,
        title: value.title,
      })),
    [config]
  );

  const trackStateChange = useCallback(
    (tabName: string, config: DashboardConfig) => {
      trackSegmentAnalyticsEvent("Dashboard - View changed", {
        tabName,
        ...config[tabName],
      });
    },
    [trackSegmentAnalyticsEvent]
  );

  const updateTabColumnsConfig = useCallback(
    (tab: string, columns: DashboardColumns) => {
      const newConfig = {
        ...config,
        [tab]: {
          ...config[tab],
          ...columns,
        },
      };

      updateConfig(newConfig);
      trackStateChange(tab, newConfig);
    },
    [trackStateChange, updateConfig, config]
  );

  const addNewTab = useCallback(() => {
    trackSegmentAnalyticsEvent("Dashboard - Create New View", {
      method: "dashboard header tab click",
    });
    showDashboardManageDrawer({ initializeNewTab: true });
  }, [trackSegmentAnalyticsEvent]);

  const openManageDrawer = useCallback(
    (tab?: string, analyticsProps?: AnalyticsCommonProps["analyticsProps"]) => {
      trackSegmentAnalyticsEvent("Dashboard - Manage view Clicked", analyticsProps);

      showDashboardManageDrawer({
        tab,
      });
    },
    [trackSegmentAnalyticsEvent]
  );

  if (noData) {
    return <NotFoundPage />;
  }

  return (
    <DashboardContext.Provider
      value={{
        updateTabColumnsConfig,
        config,
        openManageDrawer,
        tabs,
        addNewTab,
        canAddMoreViews: Object.keys(config).length < VIEWS_LIMIT,
        configLoading,
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
};

export default DashboardContextProvider;
